import React from 'react';
import { KBBlock } from '../../../types';
import { renderParagraph } from '../utils/renderInline';

interface Props {
  block: KBBlock;
  isEditing: boolean;
  onChange: (updates: Partial<KBBlock>) => void;
  onFinish: () => void;
}

type Variant = 'info' | 'warning' | 'danger' | 'success' | 'note';

const VARIANTS: {
  value: Variant;
  label: string;
  icon: string;
  bg: string;
  border: string;
  text: string;
  iconColor: string;
}[] = [
  { value: 'info',    label: 'Info',    icon: 'ri-information-line',     bg: 'bg-blue-50',   border: 'border-blue-200',  text: 'text-blue-800',  iconColor: 'text-blue-500'  },
  { value: 'warning', label: 'Warning', icon: 'ri-alert-line',            bg: 'bg-amber-50',  border: 'border-amber-200', text: 'text-amber-800', iconColor: 'text-amber-500' },
  { value: 'danger',  label: 'Danger',  icon: 'ri-error-warning-line',    bg: 'bg-red-50',    border: 'border-red-200',   text: 'text-red-800',   iconColor: 'text-red-500'   },
  { value: 'success', label: 'Success', icon: 'ri-checkbox-circle-line',  bg: 'bg-green-50',  border: 'border-green-200', text: 'text-green-800', iconColor: 'text-green-600' },
  { value: 'note',    label: 'Note',    icon: 'ri-sticky-note-line',      bg: 'bg-gray-50',   border: 'border-gray-200',  text: 'text-gray-700',  iconColor: 'text-gray-400'  },
];

const CalloutBlock: React.FC<Props> = ({ block, isEditing, onChange, onFinish }) => {
  const variant = (block.data?.variant ?? 'info') as Variant;
  const v = VARIANTS.find((x) => x.value === variant) ?? VARIANTS[0];

  if (isEditing) {
    return (
      <div className={`rounded-lg border ${v.bg} ${v.border} p-3 space-y-2`}>
        <div className="flex items-center gap-2">
          <i className={`${v.icon} ${v.iconColor} text-lg`} />
          <select
            value={variant}
            onChange={(e) => onChange({ data: { ...block.data, variant: e.target.value } })}
            className="text-sm border border-gray-200 rounded px-2 py-0.5 outline-none focus:border-blue-400 bg-white"
          >
            {VARIANTS.map((x) => (
              <option key={x.value} value={x.value}>{x.label}</option>
            ))}
          </select>
        </div>
        <textarea
          autoFocus
          value={block.content}
          placeholder="Callout content…"
          onChange={(e) => onChange({ content: e.target.value })}
          onKeyDown={(e) => { if (e.key === 'Escape') onFinish(); }}
          rows={3}
          className={`w-full bg-transparent resize-none outline-none text-sm ${v.text} placeholder-gray-400`}
        />
        <div>
          <button
            onClick={onFinish}
            className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-lg border ${v.bg} ${v.border} px-4 py-3 flex gap-2.5`}>
      <i className={`${v.icon} ${v.iconColor} text-lg flex-shrink-0 mt-0.5`} />
      <p className={`text-sm leading-relaxed m-0 ${v.text}`}>
        {block.content ? renderParagraph(block.content) : <em className="opacity-40">Empty callout</em>}
      </p>
    </div>
  );
};

export default CalloutBlock;
